import React, { useState, useEffect, useCallback } from 'react';
import './BabyManagement.css';
import { API_BASE_URL } from '../config';
import { calculateAge, formatBirthDate } from '../utils/ageCalculator';

interface Baby {
  id: string;
  name: string;
  birth_date?: string;
  user_id: string;
  created_at: string;
  is_owner?: boolean;
  owner_username?: string;
}

interface BabyManagementProps {
  user: any;
}

const BabyManagement: React.FC<BabyManagementProps> = ({ user }) => {
  const [babies, setBabies] = useState<Baby[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingBaby, setEditingBaby] = useState<Baby | null>(null);
  const [editName, setEditName] = useState('');
  const [editBirthDate, setEditBirthDate] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newBabyName, setNewBabyName] = useState('');
  const [newBabyBirthDate, setNewBabyBirthDate] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchBabies = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/babies/user/${user.id}`);
      if (response.ok) {
        const data = await response.json();
        setBabies(data);
      }
    } catch (error) {
      console.error('Failed to fetch babies:', error);
      setError('Failed to load babies');
    } finally {
      setLoading(false);
    }
  }, [user.id]);

  useEffect(() => {
    fetchBabies();
  }, [fetchBabies]);

  const startEditing = (baby: Baby) => {
    setEditingBaby(baby);
    setEditName(baby.name);
    setEditBirthDate(baby.birth_date || '');
    setError('');
  };

  const cancelEditing = () => {
    setEditingBaby(null);
    setEditName('');
    setEditBirthDate('');
    setError('');
  };

  const updateBaby = async () => {
    if (!editingBaby || !editName.trim()) {
      setError('Baby name cannot be empty');
      return;
    }

    setIsSubmitting(true);
    try {
      const updateData: any = {
        name: editName.trim(),
      };

      if (editBirthDate) {
        updateData.birth_date = editBirthDate;
      }

      const response = await fetch(`${API_BASE_URL}/api/babies/${editingBaby.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData),
      });

      if (response.ok) {
        fetchBabies();
        setEditingBaby(null);
        setEditName('');
      } else {
        throw new Error('Failed to update baby');
      }
    } catch (error) {
      setError('Failed to update baby name');
    } finally {
      setIsSubmitting(false);
    }
  };

  const addBaby = async () => {
    if (!newBabyName.trim()) {
      setError('Baby name cannot be empty');
      return;
    }

    if (!newBabyBirthDate) {
      setError('Please select a birth date');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/babies/?user_id=${user.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: newBabyName.trim(),
          birth_date: newBabyBirthDate,
        }),
      });

      if (response.ok) {
        fetchBabies();
        setShowAddForm(false);
        setNewBabyName('');
      } else {
        throw new Error('Failed to add baby');
      }
    } catch (error) {
      setError('Failed to add baby');
    } finally {
      setIsSubmitting(false);
    }
  };

  const deleteBaby = async (baby: Baby) => {
    const ownBabies = babies.filter(b => b.is_owner !== false);
    if (ownBabies.length <= 1) {
      setError('You must have at least one baby of your own');
      return;
    }

    if (baby.is_owner === false) {
      setError('You can only delete your own babies');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete "${baby.name}"? This will also delete all sleep records for this baby.`)) {
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/babies/${baby.id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        fetchBabies();
      } else {
        throw new Error('Failed to delete baby');
      }
    } catch (error) {
      setError('Failed to delete baby');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return <div className="baby-management-loading">Loading babies...</div>;
  }

  return (
    <div className="baby-management">
      <div className="section-header">
        <button
          className="add-baby-btn"
          onClick={() => setShowAddForm(true)}
          disabled={isSubmitting}
        >
          + Add Baby
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {showAddForm && (
        <div className="add-baby-form">
          <input
            type="text"
            placeholder="Baby name"
            value={newBabyName}
            onChange={(e) => setNewBabyName(e.target.value)}
            maxLength={100}
          />
          <div className="form-group">
            <label htmlFor="newBabyBirthDate">Birth Date</label>
            <input
              type="date"
              id="newBabyBirthDate"
              value={newBabyBirthDate}
              onChange={(e) => setNewBabyBirthDate(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              required
            />
          </div>
          <div className="form-actions">
            <button
              onClick={() => {
                setShowAddForm(false);
                setNewBabyName('');
                setNewBabyBirthDate('');
                setError('');
              }}
              className="cancel-btn"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              onClick={addBaby}
              className="save-btn"
              disabled={isSubmitting || !newBabyName.trim() || !newBabyBirthDate}
            >
              {isSubmitting ? 'Adding...' : 'Add'}
            </button>
          </div>
        </div>
      )}

      <div className="babies-list">
        {babies.map((baby) => (
          <div key={baby.id} className="baby-item">
            {editingBaby?.id === baby.id ? (
              <div className="edit-baby-form">
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  maxLength={100}
                  autoFocus
                />
                <div className="form-group">
                  <label htmlFor="editBirthDate">Birth Date</label>
                  <input
                    type="date"
                    id="editBirthDate"
                    value={editBirthDate}
                    onChange={(e) => setEditBirthDate(e.target.value)}
                    max={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div className="form-actions">
                  <button
                    onClick={cancelEditing}
                    className="cancel-btn"
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={updateBaby}
                    className="save-btn"
                    disabled={isSubmitting || !editName.trim()}
                  >
                    {isSubmitting ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="baby-display">
                <div className="baby-info">
                  <h4>
                    {baby.name}
                    {baby.birth_date && ` (${calculateAge(baby.birth_date)})`}
                    {baby.is_owner === false && (
                      <span className="shared-indicator">
                        (shared by {baby.owner_username})
                      </span>
                    )}
                  </h4>
                  {baby.birth_date && (
                    <p>Born: {formatBirthDate(baby.birth_date)}</p>
                  )}
                  <p>Added: {new Date(baby.created_at).toLocaleDateString()}</p>
                </div>
                {baby.is_owner !== false && (
                  <div className="baby-actions">
                    <button 
                      onClick={() => startEditing(baby)}
                      className="edit-btn"
                      disabled={isSubmitting}
                    >
                      Edit
                    </button>
                    <button 
                      onClick={() => deleteBaby(baby)}
                      className="delete-btn"
                      disabled={isSubmitting || babies.filter(b => b.is_owner !== false).length <= 1}
                      title={babies.filter(b => b.is_owner !== false).length <= 1 ? 'Cannot delete your last baby' : 'Delete baby'}
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default BabyManagement;